
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, PageHeader, Stack } from '@client/shared/design';
import { CalendarGrid, type CalendarEvent } from '@client/shared/calendar/CalendarGrid.tsx';
import { Calendar } from '@client/shared/calendar/Calendar.tsx';
import { calendarEmptyLabel } from '@client/shared/calendar/text.ts';
import {
  EventDetailCard,
  BookingDetailBody,
} from './listing-details/EventDetailCard.tsx';
import { cancelBooking } from './cancel-booking-operation.ts';
import { useCurrentUser, useHasRole } from '@client/shared/auth';
import { classifyBookingOwner, ownerCalendarTone } from '@client/shared/lib/booking-owner.ts';
import { formatViewerRange } from '@client/shared/lib/viewer-timezone.ts';
import { agentDisplayLabel } from '@client/features/users';
import { listingDisplayAddress, toBookingDisplay } from './booking-display.ts';
import { useAgentNameMap } from './agent-operations.ts';
import { graphql, gqlRequest, type ResultOf } from '@client/shared/graphql';
import { toInstant } from '@shared/booking/slots.ts';
import { viewerDayKey } from '@client/shared/calendar/week-grid.ts';
import { useCalendarAxis } from '@client/shared/hooks/use-calendar-axis.ts';
import { useCalendarView } from '@client/shared/hooks/use-calendar-view.ts';
import { viewerTimeZone } from '@client/shared/lib/viewer-timezone.ts';
import styles from './Bookings.module.css';

const BookingsPageQuery = graphql(`
  query BookingsPage {
    bookings {
      id
      listingId
      agentUserId
      startsAt
      endsAt
    }
    listings(bookableOnly: false) {
      id
      formattedAddress
      addressLine1
      city
    }
  }
`);

type BookingsPageData = ResultOf<typeof BookingsPageQuery>;
type BookingRow = NonNullable<NonNullable<BookingsPageData['bookings']>[number]>;
type ListingRow = NonNullable<NonNullable<BookingsPageData['listings']>[number]>;

const BOOKINGS_QUERY_KEY = ['bookings', 'page'] as const;

type RangeMode = 'week' | 'day';
const ALL = '__all__';

interface Filters {
  listingId: string;
  agentId: string;
}

function listingLabel(listing: ListingRow): string {
  return listingDisplayAddress(listing, `Listing ${listing.id?.slice(0, 8) ?? ''}`);
}

export function Bookings() {
  const zone = viewerTimeZone();
  const viewerUserId = useCurrentUser()?.user.id ?? null;
  const isAdmin = useHasRole('admin', 'owner');
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>({ listingId: ALL, agentId: ALL });
  const view = useCalendarView({ zone, range: 'week' });
  const { anchor, range: mode } = view;
  const axis = useCalendarAxis({ anchor, range: mode, zone });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedElRef = useRef<HTMLElement | null>(null);

  const query = useQuery({
    queryKey: BOOKINGS_QUERY_KEY,
    queryFn: ({ signal }) => gqlRequest(BookingsPageQuery, {}, { signal }),
  });

  const cancelMutation = useMutation({
    mutationFn: (bookingId: string) => cancelBooking({ bookingId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BOOKINGS_QUERY_KEY });
      setSelectedId(null);
    },
  });

  const nameMap = useAgentNameMap();

  const bookings: readonly BookingRow[] = useMemo(
    () => (query.data?.bookings ?? []).filter((b): b is BookingRow => b != null),
    [query.data],
  );
  const listings: readonly ListingRow[] = useMemo(
    () => (query.data?.listings ?? []).filter((l): l is ListingRow => l != null),
    [query.data],
  );

  const listingById = useMemo(() => {
    const map = new Map<string, ListingRow>();
    for (const l of listings) if (l.id) map.set(l.id, l);
    return map;
  }, [listings]);

  const bookingById = useMemo(() => {
    const map = new Map<string, BookingRow>();
    for (const b of bookings) if (b.id) map.set(b.id, b);
    return map;
  }, [bookings]);

  const agentOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const b of bookings) if (b.agentUserId) ids.add(b.agentUserId);
    return [...ids].sort();
  }, [bookings]);

  const visibleDayKeys = useMemo(() => new Set(axis.days), [axis.days]);
  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filters.listingId !== ALL && b.listingId !== filters.listingId) return false;
      if (filters.agentId !== ALL && b.agentUserId !== filters.agentId) return false;
      if (!b.startsAt) return false;
      const dayKey = viewerDayKey(toInstant(b.startsAt), zone);
      return visibleDayKeys.has(dayKey);
    });
  }, [bookings, filters, visibleDayKeys, zone]);

  const events: CalendarEvent[] = useMemo(() => {
    const out: CalendarEvent[] = [];
    for (const b of filtered) {
      const listing = b.listingId ? listingById.get(b.listingId) : undefined;
      const display = toBookingDisplay(
        b,
        listing,
        { viewerUserId, agentNames: nameMap },
        { fallbackAddress: listing ? `Listing ${listing.id?.slice(0, 8) ?? ''}` : 'Listing (removed)' },
      );
      if (!display) continue;
      out.push({
        id: display.id,
        start: display.start,
        end: display.end,
        title: display.address,
        subtitle: display.agentLabel ?? undefined,
        tone: ownerCalendarTone(display.owner),
      });
    }
    return out;
  }, [filtered, listingById, viewerUserId, nameMap]);

  const selectedEvent = selectedId ? events.find((e) => e.id === selectedId) ?? null : null;
  const selectedBooking = selectedId ? bookingById.get(selectedId) ?? null : null;
  const canCancelSelected =
    isAdmin || (viewerUserId != null && selectedBooking?.agentUserId === viewerUserId);

  return (
    <Stack gap={6}>
      <PageHeader
        className="reveal"
        eyebrow="Group ledger"
        title="Bookings"
        description="Every open house booked across your listings: filter by listing, agent, day, or week."
        actions={
          !query.isLoading && (
            <Badge tone="accent" dot>
              {events.length} {events.length === 1 ? 'booking' : 'bookings'}
            </Badge>
          )
        }
      />

      <section
        className="reveal"
        style={{ ['--i' as string]: 1 }}
        aria-label="Bookings calendar"
      >
        <Calendar
          zone={zone}
          anchor={anchor}
          range={mode}
          onStep={view.step}
          onToday={view.goToday}
          onRangeChange={view.setRange}
          loading={query.isLoading}
          loadingLabel="Loading bookings…"
          error={query.isError}
          errorLabel={{
            title: 'Couldn’t load bookings',
            detail: 'Something went wrong loading the bookings. Refresh the page to try again.',
          }}
          filters={
            <>
              <Select
                label="Listing"
                value={filters.listingId}
                onChange={(listingId) => setFilters((f) => ({ ...f, listingId }))}
              >
                <option value={ALL}>All listings</option>
                {listings.map((l) => (
                  <option key={l.id} value={l.id ?? ''}>
                    {listingLabel(l)}
                  </option>
                ))}
              </Select>

              <Select
                label="Agent"
                value={filters.agentId}
                onChange={(agentId) => setFilters((f) => ({ ...f, agentId }))}
              >
                <option value={ALL}>All agents</option>
                {agentOptions.map((id) => (
                  <option key={id} value={id}>
                    {agentDisplayLabel(id, null, nameMap)}
                  </option>
                ))}
              </Select>
            </>
          }
          legend={['yourBookings', 'otherAgents']}
        >
          <CalendarGrid
            events={events}
            anchor={anchor}
            range={mode}
            emptyLabel={emptyLabel(filters, mode)}
            onSelectEvent={(event, el) => {
              cancelMutation.reset();
              selectedElRef.current = el;
              setSelectedId(event.id);
            }}
          />
        </Calendar>
      </section>

      {selectedEvent && (
        <EventDetailCard
          anchorRef={selectedElRef}
          tone={classifyBookingOwner(selectedBooking?.agentUserId, viewerUserId)}
          eyebrow="Open house"
          ariaLabel="Open house"
          onClose={() => {
            cancelMutation.reset();
            setSelectedId(null);
          }}
        >
          <BookingDetailBody
            title={selectedEvent.title}
            timeRange={formatViewerRange(selectedEvent.start, selectedEvent.end)}
            meta={selectedEvent.subtitle ?? ''}
            note={
              canCancelSelected
                ? 'This frees the slot for another agent to book. Anyone notified about this listing will be told it was cancelled.'
                : 'Only an admin or owner can cancel another agent’s booking.'
            }
            canCancel={canCancelSelected}
            onCancel={async () => {
              await cancelMutation.mutateAsync(selectedEvent.id);
              await queryClient.invalidateQueries({ queryKey: BOOKINGS_QUERY_KEY });
            }}
            onClose={() => {
              cancelMutation.reset();
              setSelectedId(null);
            }}
          />
        </EventDetailCard>
      )}
    </Stack>
  );
}

function emptyLabel(filters: Filters, mode: RangeMode): string {
  const filtered = filters.listingId !== ALL || filters.agentId !== ALL;
  return calendarEmptyLabel({ scope: mode, filtered });
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}

function Select({ label, value, onChange, children }: SelectProps) {
  return (
    <span className={styles.select}>
      <select
        className={styles.selectControl}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      >
        {children}
      </select>
      <span className={styles.selectChevron} aria-hidden="true">
        ▾
      </span>
    </span>
  );
}
